import { remove } from "@src/appV2/api";
import { GET_CHAT_CHANNELS_QUERY_KEY } from "@src/appV2/Chat/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";

const LEAVE_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA = z.object({
  data: z.object({
    type: z.literal("clipboard-guides"),
    attributes: z.object({
      channelUrl: z.string(),
    }),
  }),
});

type LeaveClipboardGuidesChannelResponse = z.infer<
  typeof LEAVE_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA
>;

interface LeaveClipboardGuidesChannelVariables {
  workerId: string;
}

interface UseLeaveClipboardGuidesChannelParams {
  workplaceId: string;
  options?: UseMutationOptions<
    LeaveClipboardGuidesChannelResponse,
    unknown,
    LeaveClipboardGuidesChannelVariables
  >;
}

type UseLeaveClipboardGuidesChannelResult = UseMutationResult<
  LeaveClipboardGuidesChannelResponse,
  unknown,
  LeaveClipboardGuidesChannelVariables
>;

export function useLeaveClipboardGuidesChannel(
  params: UseLeaveClipboardGuidesChannelParams
): UseLeaveClipboardGuidesChannelResult {
  const { workplaceId, options = {} } = params;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workerId }: LeaveClipboardGuidesChannelVariables) => {
      const response = await remove({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/clipboard-guides/channels/${workplaceId}/members/${workerId}`,
        responseSchema: LEAVE_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.LEAVE_CLIPBOARD_GUIDES_CHANNEL_FAILURE,
    },
    ...options,
    onSettled: (data, error, variables, context) => {
      void queryClient.invalidateQueries(GET_CHAT_CHANNELS_QUERY_KEY);
      return options.onSettled?.(data, error, variables, context);
    },
  });
}
